// src/services/api.js
const API_BASE_URL = "https://expenseloop.ddns.net/api/expense-loop";
// const API_BASE_URL = "http://localhost:3000/api/expense-loop";

class ApiService {
  constructor() {
    this.token = localStorage.getItem("token");
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;

    // Get latest token in case it was updated
    const currentToken = localStorage.getItem("token");
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(currentToken && { Authorization: `Bearer ${currentToken}` }),
        ...options.headers,
      },
      ...options,
    };

    // Handle FormData (for file uploads)
    if (
      config.body &&
      typeof config.body === "object" &&
      !(config.body instanceof FormData)
    ) {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          this.clearAuthData();
          window.location.href = "/expense-loop/login";
        }

        throw new Error(
          data.message || `API request failed with status ${response.status}`
        );
      }

      return data;
    } catch (error) {
      console.error("API request error:", error);
      throw error;
    }
  }

  // Auth methods
  async login(credentials) {
    return this.request("/auth/login", {
      method: "POST",
      body: credentials,
    });
  }

  async register(userData) {
    return this.request("/auth/register", {
      method: "POST",
      body: userData,
    });
  }

  async registerWithOtp(userData) {
    return this.request("/auth/register-with-otp", {
      method: "POST",
      body: userData,
    });
  }

  async requestVerificationOtp(email) {
    return this.request("/auth/request-verification-otp", {
      method: "POST",
      body: { email },
    });
  }

  async verifyEmail(verificationData) {
    return this.request("/auth/verify-email", {
      method: "POST",
      body: verificationData,
    });
  }

  async requestPasswordResetOtp(email) {
    return this.request("/auth/request-password-reset-otp", {
      method: "POST",
      body: { email },
    });
  }

  async verifyPasswordResetOtp(resetData) {
    return this.request("/auth/verify-password-reset-otp", {
      method: "POST",
      body: resetData,
    });
  }

  async getCurrentUser() {
    return this.request("/auth/me");
  }

  async logout() {
    try {
      await this.request("/auth/logout", {
        method: "POST",
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      this.clearAuthData();
    }
  }

  // User profile methods
  async getProfile() {
    return this.request("/users/profile");
  }

  async updateProfile(profileData) {
    return this.request("/users/profile", {
      method: "PUT",
      body: profileData,
    });
  }

  async updateSettings(settingsData) {
    return this.request("/users/settings", {
      method: "PUT",
      body: settingsData,
    });
  }

  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append("avatar", file);

    return this.request("/users/profile/avatar", {
      method: "POST",
      headers: {
        // Don't set Content-Type for FormData, let browser set it
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: formData,
    });
  }

  async removeAvatar() {
    return this.request("/users/profile/avatar", {
      method: "DELETE",
    });
  }

  // Dashboard methods
  async getDashboardStats() {
    return this.request("/dashboard/stats");
  }

  async getDashboardEvents() {
    return this.request("/dashboard/events");
  }

  async getDashboardBalances() {
    return this.request("/dashboard/balances");
  }

  // Event methods
  async getEvents() {
    return this.request("/events");
  }

  async getEventDetails(id) {
    return this.request(`/events/${id}`);
  }

  async createEvent(eventData) {
    return this.request("/events", {
      method: "POST",
      body: eventData,
    });
  }

  async updateEvent(id, eventData) {
    return this.request(`/events/${id}`, {
      method: "PUT",
      body: eventData,
    });
  }

  async deleteEvent(id) {
    return this.request(`/events/${id}`, {
      method: "DELETE",
    });
  }

  // Expense methods
  async createExpense(eventId, expenseData) {
    return this.request(`/events/${eventId}/expenses`, {
      method: "POST",
      body: expenseData,
    });
  }

  async updateExpense(id, expenseData) {
    return this.request(`/expenses/${id}`, {
      method: "PUT",
      body: expenseData,
    });
  }

  async deleteExpense(id) {
    return this.request(`/expenses/${id}`, {
      method: "DELETE",
    });
  }

  // Balance methods
  async getBalances() {
    return this.request("/balances");
  }

  // Helper methods
  setToken(token) {
    this.token = token;
    localStorage.setItem("token", token);
  }

  clearAuthData() {
    this.token = null;
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("isAuthenticated");
  }

  isAuthenticated() {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    return !!(token && user);
  }

  // Get stored user data
  getStoredUser() {
    try {
      const userStr = localStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error("Error parsing stored user:", error);
      return null;
    }
  }

  // Store user data
  storeUser(user) {
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("isAuthenticated", "true");
  }

  async searchParticipants(eventId, searchTerm) {
    return this.request(
      `/events/${eventId}/participants/search?search=${encodeURIComponent(
        searchTerm
      )}`
    );
  }

  async addParticipant(eventId, userId) {
    return this.request(`/events/${eventId}/participants`, {
      method: "POST",
      body: { userId },
    });
  }

  async removeParticipant(eventId, userId) {
    return this.request(`/events/${eventId}/participants/${userId}`, {
      method: "DELETE",
    });
  }

  async searchUsers(searchTerm) {
    return this.request(
      `/users/search?search=${encodeURIComponent(searchTerm)}`
    );
  }

  // Settlement methods
  async createSettlement(eventId, settlementData) {
    return this.request(`/events/${eventId}/settlements`, {
      method: "POST",
      body: settlementData,
    });
  }

  async getEventSettlements(eventId) {
    return this.request(`/events/${eventId}/settlements`);
  }

  async deleteSettlement(id) {
    return this.request(`/settlements/${id}`, {
      method: "DELETE",
    });
  }

  // Analytics methods
  async getAnalyticsSummary(timeRange = "last30days") {
    return this.request(`/analytics/summary?timeRange=${timeRange}`);
  }

  async getAnalyticsMonthlyTrends(timeRange = "last30days") {
    return this.request(`/analytics/monthly-trends?timeRange=${timeRange}`);
  }

  async getAnalyticsCategoryBreakdown(timeRange = "last30days") {
    return this.request(`/analytics/category-breakdown?timeRange=${timeRange}`);
  }

  async getAnalyticsTopEvents(timeRange = "last30days") {
    return this.request(`/analytics/top-events?timeRange=${timeRange}`);
  }

  async getAnalyticsRecentActivity(timeRange = "last30days") {
    return this.request(`/analytics/recent-activity?timeRange=${timeRange}`);
  }

  async getAnalyticsDetailed(timeRange = "last30days") {
    return this.request(`/analytics/detailed?timeRange=${timeRange}`);
  }
}

// Create singleton instance
export const apiService = new ApiService();

// Export individual functions for convenience
export const authAPI = {
  login: (credentials) => apiService.login(credentials),
  register: (userData) => apiService.register(userData),
  registerWithOtp: (userData) => apiService.registerWithOtp(userData),
  requestVerificationOtp: (email) => apiService.requestVerificationOtp(email),
  verifyEmail: (verificationData) => apiService.verifyEmail(verificationData),
  requestPasswordResetOtp: (email) => apiService.requestPasswordResetOtp(email),
  verifyPasswordResetOtp: (resetData) => apiService.verifyPasswordResetOtp(resetData),
  getCurrentUser: () => apiService.getCurrentUser(),
  logout: () => apiService.logout(),
};

export const userAPI = {
  getProfile: () => apiService.getProfile(),
  updateProfile: (profileData) => apiService.updateProfile(profileData),
  updateSettings: (settingsData) => apiService.updateSettings(settingsData),
  uploadAvatar: (file) => apiService.uploadAvatar(file),
  removeAvatar: () => apiService.removeAvatar(),
};

export const dashboardAPI = {
  getStats: () => apiService.getDashboardStats(),
  getEvents: () => apiService.getDashboardEvents(),
  getBalances: () => apiService.getDashboardBalances(),
};

export const analyticsAPI = {
  getSummary: (timeRange) => apiService.getAnalyticsSummary(timeRange),
  getMonthlyTrends: (timeRange) =>
    apiService.getAnalyticsMonthlyTrends(timeRange),
  getCategoryBreakdown: (timeRange) =>
    apiService.getAnalyticsCategoryBreakdown(timeRange),
  getTopEvents: (timeRange) => apiService.getAnalyticsTopEvents(timeRange),
  getRecentActivity: (timeRange) =>
    apiService.getAnalyticsRecentActivity(timeRange),
  getDetailed: (timeRange) => apiService.getAnalyticsDetailed(timeRange),
};

export const eventAPI = {
  getAll: () => apiService.getEvents(),
  getDetails: (id) => apiService.getEventDetails(id),
  create: (eventData) => apiService.createEvent(eventData),
  update: (id, eventData) => apiService.updateEvent(id, eventData),
  delete: (id) => apiService.deleteEvent(id),
};

export const expenseAPI = {
  create: (eventId, expenseData) =>
    apiService.createExpense(eventId, expenseData),
  update: (id, expenseData) => apiService.updateExpense(id, expenseData),
  delete: (id) => apiService.deleteExpense(id),
};

export const balanceAPI = {
  getAll: () => apiService.getBalances(),
};

// Utility functions
export const setAuthData = (token, user) => {
  apiService.setToken(token);
  apiService.storeUser(user);
};

export const clearAuthData = () => {
  apiService.clearAuthData();
};

export const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default apiService;
